import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { FileText, Plus, ClipboardCheck, Clock, AlertTriangle, CheckCircle2, BarChart3 } from 'lucide-react';

export default function FormsDashboardPage() {
  const [dashboard, setDashboard] = useState(null);
  const [templates, setTemplates] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.allSettled([
      api.get('/forms/dashboard'),
      api.get('/forms/templates?pageSize=10&status=PUBLISHED'),
    ]).then(([dR, tR]) => {
      if (dR.status === 'fulfilled') setDashboard(dR.value.data.data);
      if (tR.status === 'fulfilled') setTemplates(tR.value.data.items || []);
    });
  }, []);

  const d = dashboard || {};

  return (
    <div className="space-y-6" data-testid="forms-dashboard">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><FileText className="w-6 h-6" />Forms</h1>
          <p className="text-muted-foreground mt-1">Manage digital forms across the platform</p></div>
        <Button onClick={() => navigate('/forms/templates/new')} data-testid="create-form-btn"><Plus className="w-4 h-4 mr-2" />Create Form</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Card className="cursor-pointer hover:shadow-md" onClick={() => navigate('/forms/templates')}><CardContent className="py-4 text-center"><p className="text-2xl font-bold">{d.total_templates || 0}</p><p className="text-xs text-muted-foreground">Templates</p></CardContent></Card>
        <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold text-green-600">{d.published_templates || 0}</p><p className="text-xs text-muted-foreground">Published</p></CardContent></Card>
        <Card className="cursor-pointer hover:shadow-md" onClick={() => navigate('/forms/assignments')}><CardContent className="py-4 text-center"><p className="text-2xl font-bold text-blue-600">{d.pending_assignments || 0}</p><p className="text-xs text-muted-foreground">Pending</p></CardContent></Card>
        <Card className={d.overdue_forms > 0 ? 'border-red-200' : ''}><CardContent className="py-4 text-center"><p className={`text-2xl font-bold ${d.overdue_forms > 0 ? 'text-red-600' : ''}`}>{d.overdue_forms || 0}</p><p className="text-xs text-muted-foreground">Overdue</p></CardContent></Card>
        <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold">{d.submitted_today || 0}</p><p className="text-xs text-muted-foreground">Today</p></CardContent></Card>
        <Card className="cursor-pointer hover:shadow-md" onClick={() => navigate('/forms/approvals')}><CardContent className="py-4 text-center"><p className="text-2xl font-bold text-amber-600">{d.approval_queue || 0}</p><p className="text-xs text-muted-foreground">Approvals</p></CardContent></Card>
        <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold">{d.total_submissions || 0}</p><p className="text-xs text-muted-foreground">Total Subs</p></CardContent></Card>
      </div>

      {templates.length > 0 && (
        <Card><CardHeader className="pb-2"><CardTitle className="text-base">Published Templates</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {templates.map(t => (
              <div key={t.id} className="flex items-center justify-between py-2 border-b border-dashed last:border-0 cursor-pointer hover:bg-muted/30 px-2 rounded" onClick={() => navigate(`/forms/templates/${t.id}`)}>
                <div><p className="text-sm font-medium">{t.name}</p><p className="text-xs text-muted-foreground">{t.form_code} · {t.module_type}</p></div>
                <Badge variant="default">PUBLISHED</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
